import React from 'react'
import { useTranslation } from 'react-i18next'
import {observer} from 'mobx-react'
import {Form, Input, Radio} from 'antd'

import {CONFIGS} from '@/stores/bank-account-create/services/configs'
import {SORT_CODE_PATTERN} from '@/constants/patterns'
import {SortCodeInput} from '@/components/dumb/SortCodeInput/SortCodeInput'

import type {BankAccountCreateDynamicFieldsProps} from './props'

export const DynamicFields = observer(
    ({
        isFormDisabled,
        isSoleTrader,
        isAllowedToToggleAccountType,
        isValidating,
        onFieldChange,
        form
    }: BankAccountCreateDynamicFieldsProps) => {
        const { t } = useTranslation()
        const isFieldDisabled = isValidating || isFormDisabled

        return (
            <>
                <Form.Item label={t('BankAccountCreate.labels.accountType')} name='bankAccountType'>
                    <Radio.Group disabled={isFormDisabled || !isAllowedToToggleAccountType}>
                        <Radio value='business'>{t('BankAccountCreate.options.business')}</Radio>
                        {isSoleTrader && <Radio value='personal'>{t('BankAccountCreate.options.personal')}</Radio>}
                    </Radio.Group>
                </Form.Item>

                <Form.Item
                    label={t('BankAccountCreate.labels.bankAccountNumber')}
                    name='bankAccountNumber'
                    rules={[
                        {
                            required: true,
                            message: t('BankAccountCreate.validation.required.accountNumber')
                        },
                        {
                            min: CONFIGS.minAccountNumberLength,
                            message: t('BankAccountCreate.validation.format.accountNumberTooShort')
                        }
                    ]}
                >
                    <Input
                        placeholder={t('BankAccountCreate.placeholders.accountNumber')}
                        onChange={(e) => {
                            e.target.value = e.target.value.replace(/\D/g, '')
                            onFieldChange(e)
                        }}
                        maxLength={8}
                        disabled={isFieldDisabled}
                        onKeyPress={(e) => {
                            if (!/\d/.test(e.key)) {
                                e.preventDefault()
                            }
                        }}
                        onPaste={(e) => {
                            const pastedText = e.clipboardData.getData('text')

                            if (/[^0-9]/.test(pastedText)) {
                                e.preventDefault()
                            }
                        }}
                    />
                </Form.Item>

                <Form.Item
                    label={t('BankAccountCreate.labels.sortCode')}
                    name='sortCode'
                    rules={[
                        {
                            required: true,
                            message: t('BankAccountCreate.validation.required.sortCode')
                        },
                        {
                            pattern: SORT_CODE_PATTERN,
                            message: t('BankAccountCreate.validation.format.sortCodeFormat')
                        }
                    ]}
                    validateTrigger={['onChange', 'onBlur']}
                >
                    <SortCodeInput
                        placeholder={t('BankAccountCreate.placeholders.sortCode')}
                        disabled={isFieldDisabled}
                        form={form}
                        name='sortCode'
                        onFieldChange={onFieldChange}
                    />
                </Form.Item>
            </>
        )
    }
)
